'use client'

import { type ReactNode } from 'react'
import { CheckIcon, ChevronRightIcon, CopyIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useCopyToClipboard } from '@/hooks/use-copy-to-clipboard'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { cn } from '@/lib/utils'
import { collapsePanel, field, mono, ShimmerLabel, SwapLabel } from './surfaces'

export interface ToolCallProps {
  label: string
  activeLabel: string
  query: string
  /** Human-readable request/result; the raw JSON goes in requestJson/resultJson. */
  request: ReactNode
  result: ReactNode
  requestJson?: string
  resultJson?: string
  running: boolean
  open: boolean
  onOpenChange: (open: boolean) => void
  className?: string
}

/** Section label with a copy-raw-JSON icon on the right. */
function SectionHeader({ label, json }: { label: string; json?: string }) {
  const { t } = useTranslation('chat')
  const { isCopied, copyToClipboard } = useCopyToClipboard()
  return (
    <div className="mb-1 flex items-center justify-between">
      <p className={cn(mono, 'text-foreground/35')}>{label}</p>
      {json && (
        <button
          type="button"
          aria-label={
            isCopied ? t('steps.copied') : t('steps.copyJson', { section: label.toLowerCase() })
          }
          onClick={() => copyToClipboard(json)}
          className={cn(
            'text-foreground/35 hover:text-foreground/80 flex size-5 items-center justify-center rounded transition-colors',
            isCopied && 'text-emerald-500 hover:text-emerald-500'
          )}
        >
          {isCopied ? <CheckIcon className="size-3" /> : <CopyIcon className="size-3" />}
        </button>
      )}
    </div>
  )
}

export function ToolCall({
  label,
  activeLabel,
  query,
  request,
  result,
  requestJson,
  resultJson,
  running,
  open,
  onOpenChange,
  className,
}: ToolCallProps) {
  const { t } = useTranslation('chat')
  return (
    <Collapsible
      data-slot="tool-call"
      open={open}
      onOpenChange={onOpenChange}
      className={cn('w-full max-w-sm', className)}
    >
      <CollapsibleTrigger className="group/trigger text-foreground/55 hover:text-foreground/90 flex items-center gap-2 rounded-md py-1 text-[13.5px] transition-colors outline-none">
        <ChevronRightIcon className="size-3.5 shrink-0 opacity-60 transition-transform duration-200 ease-[cubic-bezier(0.32,0.72,0,1)] group-data-open/trigger:rotate-90 group-data-panel-open/trigger:rotate-90 motion-reduce:transition-none" />
        <SwapLabel active={running ? 0 : 1} className="text-start">
          <ShimmerLabel active={running} className="relative inline-block leading-none">
            {activeLabel}
          </ShimmerLabel>
          <>{label}</>
        </SwapLabel>
        {query && (
          <span
            className={cn(mono, 'bg-foreground/[0.06] text-foreground/70 rounded-md px-1.5 py-0.5')}
          >
            {query}
          </span>
        )}
        <span className="ms-auto flex w-4 items-center justify-end">
          {!running && (
            <CheckIcon className="fade-in zoom-in-90 animate-in size-3.5 text-emerald-500 duration-200" />
          )}
        </span>
      </CollapsibleTrigger>
      <CollapsibleContent className={cn(collapsePanel, 'outline-none')}>
        <div className={cn(field, 'mt-2 overflow-hidden rounded-2xl text-xs')}>
          <div className="px-3.5 pt-2.5 pb-2">
            <SectionHeader label={t('steps.request')} json={requestJson} />
            <div className="text-foreground/70">{request}</div>
          </div>
          <div className="bg-foreground/[0.06] mx-3.5 h-px" />
          <div className="px-3.5 pt-2 pb-2.5">
            <SectionHeader label={t('steps.result')} json={resultJson} />
            <div className="text-foreground/90">{result}</div>
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  )
}
